import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Users, Clock, ArrowRight, Circle } from 'lucide-react';
import { waitlistAPI } from '../../services/api';

const BARBERS = ['Pete', 'Mckee', 'Chris', 'First Available'];

const formatWait = (minutes) => {
  if (minutes === 0) return 'No wait';
  if (minutes < 60) return `~${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m === 0 ? `~${h} hr` : `~${h}h ${m}m`;
};

export const LiveWaitlistPreview = () => {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const result = await waitlistAPI.getPublicSummary();
      setData(result);
    } catch (err) {
      setData(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const shop = data?.shop || {};
  const isOpen = shop.is_open;
  const totalWaiting = data?.total_waiting ?? 0;

  return (
    <section id="live-waitlist" className="py-20 bg-stone-700 relative overflow-hidden">
      {/* Subtle diagonal pattern background */}
      <div className="absolute inset-0 opacity-5" aria-hidden="true">
        <div className="absolute inset-0" style={{
          backgroundImage: `repeating-linear-gradient(45deg, transparent, transparent 35px, rgba(255,255,255,.03) 35px, rgba(255,255,255,.03) 70px)`
        }}></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
          <div>
            <div className="inline-flex items-center gap-2 mb-3">
              <span className="relative flex h-3 w-3">
                {isOpen && (
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                )}
                <span className={`relative inline-flex rounded-full h-3 w-3 ${
                  isOpen ? 'bg-green-500' : 'bg-stone-500'
                }`}></span>
              </span>
              <span className="text-xs font-semibold uppercase tracking-widest text-stone-300">
                {isOpen ? 'Live Now' : shop.is_closed_today ? 'Closed Today' : 'Closed for the Day'}
              </span>
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-stone-100 mb-2">
              Current Waitlist
            </h2>
            <p className="text-stone-400 max-w-xl">
              {isOpen
                ? 'See who\'s in line right now and plan your visit. Updates every 30 seconds.'
                : 'Check back during business hours (Tue-Fri 9:00 AM - 5:30 PM) to see the live queue.'}
            </p>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            <div className="text-right">
              <p className="text-xs uppercase tracking-wide text-stone-400">Total in line</p>
              <p className="text-4xl font-bold text-stone-100" data-testid="live-total-waiting">
                {isLoading ? '—' : totalWaiting}
              </p>
            </div>
          </div>
        </div>

        {/* Barber cards grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {BARBERS.map((barber) => {
            const info = data?.barbers?.[barber] || { count: 0, estimated_wait_minutes: 0, exceeds_closing: false };
            const isFirstAvailable = barber === 'First Available';

            return (
              <Card
                key={barber}
                className={`group border-2 transition-all duration-300 ${
                  isFirstAvailable
                    ? 'bg-maroon-900/40 border-maroon-800 hover:border-maroon-700'
                    : 'bg-stone-600/50 border-stone-500 hover:border-maroon-700'
                }`}
                data-testid={`live-card-${barber.toLowerCase().replace(' ', '-')}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <p className="text-xs uppercase tracking-wide text-stone-400 mb-1">
                        {isFirstAvailable ? 'Any Barber' : 'Barber'}
                      </p>
                      <h3 className="text-xl font-bold text-stone-100">{barber}</h3>
                    </div>
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-transform group-hover:scale-110 ${
                      info.count === 0 ? 'bg-stone-500/50' : 'bg-maroon-800'
                    }`}>
                      <Users className="h-5 w-5 text-stone-100" />
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div>
                      <p className="text-xs text-stone-400 uppercase tracking-wide">In Line</p>
                      <p className="text-3xl font-bold text-stone-100">
                        {isLoading ? '—' : info.count}
                        <span className="text-sm font-normal text-stone-400 ml-2">
                          {info.count === 1 ? 'person' : 'people'}
                        </span>
                      </p>
                    </div>

                    <div className="pt-3 border-t border-stone-500/50">
                      <p className="text-xs text-stone-400 uppercase tracking-wide mb-1 flex items-center gap-1.5">
                        <Clock className="h-3 w-3" />
                        Est. Wait
                      </p>
                      <p className={`text-lg font-semibold ${
                        !isOpen
                          ? 'text-stone-500'
                          : info.exceeds_closing
                            ? 'text-amber-400'
                            : 'text-stone-100'
                      }`}>
                        {isLoading
                          ? '—'
                          : !isOpen
                            ? 'Closed'
                            : info.exceeds_closing
                              ? 'Full for today'
                              : formatWait(info.estimated_wait_minutes)}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* CTAs */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <Link to="/waitlist" className="w-full sm:w-auto">
            <Button
              size="lg"
              variant="outline"
              className="w-full sm:w-auto border-2 border-stone-400 text-stone-100 hover:bg-stone-600 hover:border-maroon-700 px-8 py-6 text-base font-semibold group"
              data-testid="view-full-waitlist-btn"
            >
              View Full Waitlist
              <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
          </Link>
          {isOpen && (
            <Button
              onClick={() => {
                const el = document.getElementById('contact');
                if (el) el.scrollIntoView({ behavior: 'smooth' });
              }}
              size="lg"
              className="w-full sm:w-auto bg-maroon-800 hover:bg-maroon-900 text-stone-100 px-8 py-6 text-base font-semibold"
              data-testid="live-join-waitlist-btn"
            >
              Join the Waitlist
            </Button>
          )}
        </div>
      </div>
    </section>
  );
};
